import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, IconButton } from '@mui/material';

import withBoxStyle from '../../HOC/layout/withBoxStyle';
import imgLow from '/images/feeling-low@2x.png';
import imgSomewhatIll from '/images/feeling-somewhat-ill@2x.png';
import imgAlright from '/images/feeling-alright@2x.png';
import imgGreat from '/images/feeling-great@2x.png';
import { useAppSelector } from '../../redux/hooks';
import useUpdateFeelingSurvey from '../../hooks/queries/useUpdateFeelingSurvey';
import { CloseIcon } from '../../components/shared/CustomIcons';
import useFetchHealthStatus from '../../hooks/queries/useFetchHealthStatus';

const profileBoxStyles = {
  maxWidth: '100%',
  minHeight: 193,
  gap: 3,
  borderRadius: 8,
  pt: 4,
  px: 2.25,
  pb: 3,
};

const HowAreYouFeeling = withBoxStyle(() => {
  const [mood, setMood] = useState<string>('');
  const patientId = useAppSelector((state) => state.auth.currentUser?.id);
  const { mutate } = useUpdateFeelingSurvey();
  const navigate = useNavigate();

  const getFormattedDate = () => {
    const today = new Date();
    return today.toLocaleDateString('en-CA'); // 'en-CA' formats as 'YYYY-MM-DD'
  };

  const { healthStatus } = useFetchHealthStatus(patientId, getFormattedDate());

  const handleSubmit = () => {
    if (mood && patientId)
      mutate({
        patientId,
        mood,
        date: getFormattedDate(),
      });
  };

  useEffect(() => {
    if (healthStatus && healthStatus?.mood) {
      setMood(healthStatus?.mood);
      navigate('/survey/track');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthStatus]);

  return (
    <Box className="d-flex flex-column flex-1">
      <Box sx={{ pt: 8, px: 3, pb: 3.5 }} className="d-flex flex-column">
        <IconButton
          aria-label="back"
          sx={{ color: 'common.white', p: 0, mb: 4.5, alignSelf: 'end' }}
          onClick={() => {
            navigate('/dashboard');
          }}
        >
          <CloseIcon sx={{ fontSize: 40, color: 'text.primary' }} />
        </IconButton>
        <Typography variant="h4" align="center">
          How are you feeling today?
        </Typography>
      </Box>
      <Box
        sx={{
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '2px',
        }}
        className="d-grid"
      >
        {[
          {
            img: imgGreat,
            label: 'great',
            value: 'great',
            labelColor: '#bb5ee8',
          },
          {
            img: imgAlright,
            label: 'ok',
            value: 'alright',
            labelColor: '#426d27',
          },
          { img: imgLow, label: 'low', labelColor: 'info.main', value: 'low' },
          {
            img: imgSomewhatIll,
            label: 'somewhat ill',
            labelColor: '#9f8900',
            value: 'somewhat_ill',
          },
        ].map(({ img, label, labelColor, value }, index) => (
          <Box
            key={index}
            sx={[
              profileBoxStyles,
              { bgcolor: mood != value ? 'common.white' : 'paleBlue.main' },
            ]}
            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
            onClick={() => setMood(value)}
          >
            <img src={img} alt={`Feeling ${label}`} width="70px" />
            <Box
              sx={{ fontWeight: 600, textAlign: 'center' }}
              className="d-flex flex-column"
            >
              <Typography>Today I'm feeling</Typography>
              <Typography sx={{ color: labelColor }}>{label}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ pt: 3.5, px: 3, pb: 4.5 }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          type="submit"
          sx={{ fontWeight: 400 }}
          onClick={handleSubmit}
        >
          Confirm
        </Button>
      </Box>
    </Box>
  );
});

export default HowAreYouFeeling;
